import Ajv2020, { type ValidateFunction } from 'ajv/dist/2020'
import addFormats from 'ajv-formats'
import bundledFaq from '../../content/faq.json'
import faqSchema from '../../content/schemas/faq.schema.json'
import bundledNotices from '../../content/notices.json'
import noticesSchema from '../../content/schemas/notices.schema.json'
import bundledProgram from '../../content/program.json'
import programSchema from '../../content/schemas/program.schema.json'
import type {
  ContentBundle,
  ContentSource,
  FaqContent,
  NoticeContent,
  ProgramContent,
} from '../types/content'

const ajv = new Ajv2020({ allErrors: true, strict: false })
addFormats(ajv)

const validateProgram = ajv.compile(programSchema) as ValidateFunction<ProgramContent>
const validateFaq = ajv.compile(faqSchema) as ValidateFunction<FaqContent>
const validateNotices = ajv.compile(noticesSchema) as ValidateFunction<NoticeContent>

const CACHE_PREFIX = 'gg-transport-content:'

interface LoadResult<T> {
  data: T
  source: ContentSource
  hadIssue: boolean
}

function readCache<T>(key: string, validate: ValidateFunction<T>): T | null {
  try {
    const value = localStorage.getItem(`${CACHE_PREFIX}${key}`)
    if (!value) return null
    const parsed: unknown = JSON.parse(value)
    return validate(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeCache(key: string, value: unknown) {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(value))
  } catch {
    // Storage can be unavailable in private browsing. Bundled content remains usable.
  }
}

async function fetchJson(path: string): Promise<unknown> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 5000)
  try {
    const response = await fetch(path, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
    if (!response.ok) throw new Error(`Content request failed: ${response.status}`)
    return response.json()
  } finally {
    window.clearTimeout(timeout)
  }
}

async function loadOne<T>(
  key: string,
  path: string,
  bundled: T,
  validate: ValidateFunction<T>,
): Promise<LoadResult<T>> {
  try {
    const remote = await fetchJson(path)
    if (!validate(remote)) throw new Error(`Invalid content: ${key}`)
    writeCache(key, remote)
    return { data: remote, source: 'remote', hadIssue: false }
  } catch {
    const cached = readCache(key, validate)
    if (cached) return { data: cached, source: 'cache', hadIssue: true }
    return { data: bundled, source: 'bundled', hadIssue: true }
  }
}

export async function loadContent(): Promise<ContentBundle> {
  const [program, faq, notices] = await Promise.all([
    loadOne('program', '/program.json', bundledProgram as ProgramContent, validateProgram),
    loadOne('faq', '/faq.json', bundledFaq as FaqContent, validateFaq),
    loadOne('notices', '/notices.json', bundledNotices as NoticeContent, validateNotices),
  ])

  const results = [program, faq, notices]
  const source: ContentSource = results.every((result) => result.source === 'remote')
    ? 'remote'
    : results.some((result) => result.source === 'cache')
      ? 'cache'
      : 'bundled'

  return {
    program: program.data,
    faq: faq.data,
    notices: notices.data,
    source,
    hasRefreshIssue: results.some((result) => result.hadIssue),
  }
}

export function validateBundledContent() {
  return {
    program: validateProgram(bundledProgram),
    faq: validateFaq(bundledFaq),
    notices: validateNotices(bundledNotices),
  }
}

